import moment from "moment";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

import { LeadService } from "../../../../services/LeadService";
import { AlboQueryService } from "../../../../services/AlboQueryService";
import { ItemType } from "../../../../util/hooks/useCatalog";

import { ChangeAlertCallbackType } from "../../../Root";
import { ResponsibleCell } from "../ResponsibleCell";
import { FilterLeadType, LeadDataType } from "../types";
import { Visibility, Loop } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";

moment.locale('es');

export const isValidFilters = (filters?: FilterLeadType) => {
    if (!filters)
        return false;
    if (!filters?.dates)
        return false;

    return true
}

export const useLeadTable = (changeAlert: ChangeAlertCallbackType, defaultFilters: FilterLeadType, status: ItemType[], changeWidgets, config) => {

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState<FilterLeadType>();
    const leadService = useRef<LeadService>();
    const alboQueryService = useRef<AlboQueryService>();

    const getData = async (newPage, newFilters?: FilterLeadType) => {
        setLoading(true);

        const response = await leadService.current.getLeadsByFilters({
            filters: newFilters || filters,
            pagination: {
                page_number: newPage,
                results_per_page: perPage
            }
        })

        setData(response.data?.content?.leads || []);
        setTotalRows(response.data?.content?.total_documents || 0);
        setLoading(false);
    };
    
    const handlePageChange = newPage => {
        getData(newPage);
        setPage(newPage)
    };
    
    const handlePerRowsChange = async (newPerPage, newPage) => {
        setLoading(true);
        const response = await leadService.current.getLeadsByFilters({
            filters, pagination: {
                page_number: newPage,
                results_per_page: newPerPage
            }
        })
        
        setData(response.data?.content?.leads || []);
        setTotalRows(response.data?.content?.total_documents || 0);
        setPerPage(newPerPage);
        setPage(newPage)
        setLoading(false);
    };
    const handleFiltersChange = async (newFilters: FilterLeadType) => {
        setFilters(newFilters);
        getData(page, newFilters)
    }
    const handleSectionChange = useCallback( async (lead: LeadDataType) => {
        const widget = {
            type: 'leadsOnboardingSections',
            data: lead,
            config: config
        }
        return changeWidgets([widget])
        
    },[changeWidgets, config])

    const dataReuseLead = useCallback( async (lead: String) => {
        setLoading(true);

        let user = JSON.parse(localStorage.session);
        const request = await alboQueryService.current.executeQuery({
            aql: `from data_reuse_lead where lead_uuid=${lead}`,
            agent: user["uuid"]
        })
    
        if (request.data.response === "OK") {
            changeAlert(true, "success", "Los datos del lead fueron liberados exitosamente");
            setLoading(false);
            return changeWidgets([request.data.widget[0]])
        } else {
          let detail = request.data.message;
          changeAlert(true, "error", "Error al ejecutar consulta: " + detail);
        }
        setLoading(false);
    },[changeWidgets, changeAlert])

    const hasAction = (action) => {
        return config?.actions?.indexOf(action) > -1;
    };

    useEffect(() => {
        leadService.current = new LeadService(changeAlert)
        alboQueryService.current = new AlboQueryService(changeAlert)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (isValidFilters(defaultFilters)) {
            handleFiltersChange(defaultFilters)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFilters])

    const calculateColumns = useMemo(() => {
        const dictionaryStatus = Object.fromEntries(status.map(d => [d.name, d.metadata?.description]));
        const columns = [
            { selector: (row) => moment(row.cdate).format("L"), name: "Fecha", wrap: true, width: "10%", sortable: false },
            { selector: (row) => row.email, name: "Correo de registro", wrap: true, width: "20%", sortable: false },
            { selector: (row) => row.social_reason, name: "Razón Social", wrap: true, width: "16%", sortable: false },
            { selector: (row) => row.type, name: "Tipo", wrap: true, width: "7%", sortable: false },
            { selector: (row) => row.tax_id, name: "RFC", wrap: true, width: "14%", sortable: false },
            { selector: (row) => dictionaryStatus[row.status] || "", name: "Estatus", wrap: true, width: "11%", sortable: false },
            {
                name: "Responsable", wrap: true, width: "13%", sortable: false,
                cell: row => <ResponsibleCell row={row} />
            },
            {
                cell: (row) => {
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    return <a href="#" onClick={handleSectionChange.bind(this, row)} title="Ver más"><Visibility /></a>
                },
                name: "", width: "3%", wrap: true, sortable: false
            },
            {
                cell: (row) => {
                    return hasAction("dataReuseLead") && <Tooltip title="Liberar datos">
                        <IconButton onClick={dataReuseLead.bind(this, row.uuid)}><Loop/></IconButton>
                    </Tooltip>
                },
                name: "", width: "3%", wrap: true, sortable: false
            },
        ]
        return columns

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, handleSectionChange, dataReuseLead])

    return { columns: calculateColumns, data, loading, totalRows, perPage, handlePageChange, handlePerRowsChange, filters, handleFiltersChange ,page}
}
